/**
 * SortableGroupHeader — wraps ContainerGroupHeader with `useSortable`
 * so it participates in the SortableContext's layout animation.
 *
 * `disabled: true` means this item can never be picked up / dragged,
 * but it still receives sort-transform/transition from @dnd-kit when
 * sibling items shift (e.g. a task is dragged into this group from
 * another container). This eliminates the "jump" on group headers.
 */

import { memo } from "react"
import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { ContainerGroupHeader } from "@/features/views/components/ContainerGroupHeader"
import { ObjectType } from "@/db/schema"
import type { ContainerProgress } from "@/features/tags/ancestorTagStore"

/** Prefix used to distinguish header IDs from task IDs in SortableContext */
export const HEADER_ID_PREFIX = "header:"

export function headerSortableId(containerId: string): string {
  return `${HEADER_ID_PREFIX}${containerId}`
}

interface SortableGroupHeaderProps {
  containerId: string
  title: string
  type: ObjectType
  progress: ContainerProgress
  taskCount: number
}

export const SortableGroupHeader = memo(function SortableGroupHeader({
  containerId,
  title,
  type,
  progress,
  taskCount,
}: SortableGroupHeaderProps) {
  const id = headerSortableId(containerId)

  const { setNodeRef, transform, transition } = useSortable({
    id,
    disabled: true,
  })

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
  }

  return (
    <div ref={setNodeRef} style={style}>
      <ContainerGroupHeader
        containerId={containerId}
        title={title}
        type={type}
        progress={progress}
        taskCount={taskCount}
      />
    </div>
  )
})
